import { getIsUserLoggedIn, getCustomerId } from '@helpers/session/user'
import { isBrowser, getAnalyticsCountryCode } from '@helpers/env'
import trackError, { trackMessage } from '@helpers/trackError'
import { STORE_ANALYTICS_CODES } from '@consts/index'

type AnalyticsCommonProperties = { url: string } & {
  store_location: keyof typeof STORE_ANALYTICS_CODES
  logged_in: boolean
}

type AnalyticsProperties =
  | AnalyticsCommonProperties
  | { [key: string]: unknown }

type AnalyticsOpts = {
  [key: string]: unknown
}

type PageProperties =
  | (AnalyticsCommonProperties & {
      path: string
      referrer?: string
    })
  | { [key: string]: unknown }

const POSTHOG_READY_TIMEOUT_MS = 2000
const POSTHOG_TIMED_OUT = Symbol('posthog-timed-out')
const POSTHOG_TIMEOUT_REPORT_SAMPLE_RATE = 0.1

async function awaitPostHogReadiness() {
  if (!window.postHogAsyncLoader) return

  const result = await Promise.race([
    window.postHogAsyncLoader,
    new Promise(resolve =>
      setTimeout(() => resolve(POSTHOG_TIMED_OUT), POSTHOG_READY_TIMEOUT_MS)
    )
  ])

  if (result === POSTHOG_TIMED_OUT && !window.postHogAnalyticsTimeoutReported) {
    window.postHogAnalyticsTimeoutReported = true

    if (Math.random() < POSTHOG_TIMEOUT_REPORT_SAMPLE_RATE) {
      trackMessage('PostHog readiness timeout hit for analytics event', {
        section: 'Analytics',
        timeoutMs: String(POSTHOG_READY_TIMEOUT_MS)
      })
    }
  }
}

async function safelySendAnalytics(callback: () => void) {
  if (isBrowser() && !!window.analytics) {
    try {
      await awaitPostHogReadiness()
    } catch (e) {
      const error = e as Error
      trackError(new Error('Failed to send analytics event'), {
        error,
        section: 'Analytics'
      })
    }

    callback()
  }
}

export async function analytics(
  name: string,
  props: AnalyticsProperties = {},
  opts: AnalyticsOpts = {}
) {
  const pageUrl = isBrowser() ? window.location.href : ''
  const eventProps = {
    ...props,
    store_location: getAnalyticsCountryCode(),
    logged_in: isBrowser() && getIsUserLoggedIn(),
    url: props.url || pageUrl
  }

  return safelySendAnalytics(() => {
    window.analytics?.track(name, eventProps, opts)
  })
}

export async function page(props: PageProperties = {}) {
  const pageUrl = isBrowser() ? window.location.href : ''

  const pageProperties = {
    ...props,
    store_location: getAnalyticsCountryCode(),
    url: props.url || pageUrl,
    logged_in: getIsUserLoggedIn(),
    referrer: props.referrer !== props.url ? props.referrer : null
  }

  return safelySendAnalytics(() => {
    window.analytics?.page(pageProperties)
  })
}

export async function identify(
  id: string | number | null | void,
  props: AnalyticsProperties = {},
  opts: AnalyticsOpts = {}
) {
  const eventProps = {
    ...props,
    store_location: getAnalyticsCountryCode(),
    logged_in: isBrowser() && getIsUserLoggedIn()
  }

  const userId = id || getCustomerId()

  if (userId) {
    return safelySendAnalytics(() => {
      window.analytics?.identify(userId, eventProps, opts)
    })
  }
}

export function analyticsPostHogOmit(
  name: string,
  props: Record<string, unknown>,
  opts?: Record<string, unknown>
) {
  analytics(name, props, {
    integrations: {
      PostHog: false
    },
    ...opts
  })
}
